/* Suspend / maintenance — full-screen page behind the amber dashboard card.
 *
 * This page is about consequence, not severity: a bus off the road matters
 * because children still need collecting. So the fleet split leads, and each
 * bus is shown with the roster it is not currently carrying.
 *
 * Status history lives in `busStatusEvents` (append-only). `buses.status` is
 * only the current state — it is overwritten on activate, so without that
 * collection a suspension that later ends leaves no trace. Events exist from
 * the day this shipped forward; older stints have no rows.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { FaArrowLeft, FaSearch, FaCheck, FaBus, FaWrench, FaPauseCircle, FaTag } from 'react-icons/fa';
import { db } from '../../firebase';
import { BUS_STATUS_EVENTS } from '../../fleet/busStatusEvents';
import { monogram } from '../../fleet/alertMeta';
import { useLockBodyScroll } from '../../fleet/useLockBodyScroll';

export interface MaintenanceBus {
  id: string;
  name: string;
  plate: string;
  status: string;
  drivers: string[];
  childCount: number;
  /** 30-day distance, so a long-idle bus is visible as a flat number. */
  km30d: number;
  lastTripFinished: Date | null;
}

export interface MaintenanceScreenProps {
  buses: MaintenanceBus[];
  orgCode: string | null;
  onClose: () => void;
  onOpenBus: (busId: string) => void;
}

export type StatusEventRow = {
  id: string;
  busId: string;
  plate: string;
  busName: string;
  fromStatus: string;
  toStatus: string;
  at: Date;
  byEmail: string | null;
  source: string;
  childCount: number;
};

const STATUS_META: Record<string, { label: string; pill: string; dot: string; ig: string; icon: React.ReactElement }> = {
  maintenance: { label: 'Maintenance', pill: 'fd-pill-warn', dot: 'fd-dot-warn', ig: 'fd-ig-warn', icon: <FaWrench /> },
  suspended:   { label: 'Suspended',   pill: 'fd-pill-crit', dot: 'fd-dot-crit', ig: 'fd-ig-crit', icon: <FaPauseCircle /> },
  for_hire:    { label: 'For hire',    pill: 'fd-pill-info', dot: 'fd-dot-brand', ig: 'fd-ig-brand', icon: <FaTag /> },
  active:      { label: 'In service',  pill: 'fd-pill-ok',   dot: 'fd-dot-ok',   ig: 'fd-ig-ok',   icon: <FaBus /> },
};

function metaFor(status: string) {
  return STATUS_META[status] ?? STATUS_META.maintenance;
}

function isOffRoad(status: string) {
  return status === 'maintenance' || status === 'suspended' || status === 'for_hire';
}

function eventTitle(toStatus: string) {
  if (toStatus === 'active') return 'Back in service';
  if (toStatus === 'for_hire') return 'Marked for hire';
  if (toStatus === 'suspended') return 'Suspended';
  if (toStatus === 'maintenance') return 'Maintenance';
  return metaFor(toStatus).label;
}

function tlClass(toStatus: string) {
  if (toStatus === 'active') return 'fd-tlrow-ok';
  if (toStatus === 'suspended') return 'fd-tlrow-crit';
  if (toStatus === 'for_hire') return 'fd-tlrow-brand';
  return 'fd-tlrow-warn';
}

function formatAge(from: Date, now = new Date()): string {
  const ms = Math.max(0, now.getTime() - from.getTime());
  const d = Math.floor(ms / 86_400_000);
  const h = Math.floor((ms % 86_400_000) / 3_600_000);
  if (d >= 14) return `${d} days`;
  if (d >= 1) return h ? `${d}d ${h}h` : `${d} day${d === 1 ? '' : 's'}`;
  if (h >= 1) {
    const m = Math.floor((ms % 3_600_000) / 60_000);
    return m ? `${h}h ${m}m` : `${h}h`;
  }
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'Just now';
  return `${m} min`;
}

function formatWhen(at: Date): string {
  return at.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function sourceLabel(source: string) {
  if (source === 'zana') return 'Zana';
  if (source === 'hire_expiry') return 'Hire expired';
  return 'Dashboard';
}

/** Newest event that put this bus into its current off-road status. */
function offSince(busId: string, status: string, events: StatusEventRow[]): Date | null {
  if (!isOffRoad(status)) return null;
  for (const e of events) {
    if (e.busId !== busId) continue;
    if (isOffRoad(e.toStatus)) return e.at;
    return null;
  }
  return null;
}

export default function MaintenanceScreen({ buses, orgCode, onClose, onOpenBus }: MaintenanceScreenProps) {
  useLockBodyScroll();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'off' | 'maintenance' | 'suspended' | 'for_hire' | 'all'>('off');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [events, setEvents] = useState<StatusEventRow[]>([]);
  const [historyReady, setHistoryReady] = useState(false);

  useEffect(() => {
    if (!orgCode) {
      setEvents([]);
      setHistoryReady(true);
      return;
    }
    setHistoryReady(false);
    const q = query(collection(db, BUS_STATUS_EVENTS), where('orgCode', '==', orgCode));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows: StatusEventRow[] = snap.docs.map((d) => {
          const data = d.data() as Record<string, unknown>;
          const atRaw = data.at as { toDate?: () => Date } | undefined;
          return {
            id: d.id,
            busId: String(data.busId || ''),
            plate: String(data.plate || ''),
            busName: String(data.busName || ''),
            fromStatus: String(data.fromStatus || ''),
            toStatus: String(data.toStatus || ''),
            at: atRaw?.toDate?.() ?? new Date(0),
            byEmail: typeof data.byEmail === 'string' && data.byEmail ? data.byEmail : null,
            source: String(data.source || 'dashboard'),
            childCount: typeof data.childCount === 'number' ? data.childCount : 0,
          };
        });
        rows.sort((a, b) => b.at.getTime() - a.at.getTime());
        setEvents(rows);
        setHistoryReady(true);
      },
      (err) => {
        console.error('busStatusEvents read failed', err);
        setEvents([]);
        setHistoryReady(true);
      },
    );
    return () => unsub();
  }, [orgCode]);

  const counts = useMemo(() => {
    const c = { active: 0, maintenance: 0, suspended: 0, for_hire: 0 };
    for (const b of buses) {
      if (b.status === 'maintenance') c.maintenance++;
      else if (b.status === 'suspended') c.suspended++;
      else if (b.status === 'for_hire') c.for_hire++;
      else c.active++;
    }
    return { ...c, off: c.maintenance + c.suspended + c.for_hire, total: buses.length };
  }, [buses]);

  /** Children whose bus is not currently in service — the real cost of the row. */
  const childrenAffected = useMemo(
    () => buses.filter((b) => b.status !== 'active').reduce((s, b) => s + b.childCount, 0),
    [buses],
  );

  const ordered = useMemo(() => {
    const rank: Record<string, number> = { suspended: 0, maintenance: 1, for_hire: 2, active: 3 };
    return [...buses].sort(
      (a, b) => (rank[a.status] ?? 3) - (rank[b.status] ?? 3) || b.childCount - a.childCount,
    );
  }, [buses]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ordered.filter((b) => {
      if (filter === 'off' && b.status === 'active') return false;
      if (filter !== 'off' && filter !== 'all' && b.status !== filter) return false;
      if (!q) return true;
      return (
        b.name.toLowerCase().includes(q) ||
        b.plate.toLowerCase().includes(q) ||
        b.drivers.join(' ').toLowerCase().includes(q)
      );
    });
  }, [ordered, filter, search]);

  const selected = useMemo(
    () => visible.find((b) => b.id === selectedId) ?? visible[0] ?? null,
    [visible, selectedId],
  );

  const grouped = useMemo(() => {
    const out: { label: string; items: MaintenanceBus[] }[] = [];
    for (const b of visible) {
      const label = metaFor(b.status).label;
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(b);
      else out.push({ label, items: [b] });
    }
    return out;
  }, [visible]);

  const longestOff = useMemo(() => {
    let best: { plate: string; status: string; since: Date; ms: number } | null = null;
    const now = Date.now();
    for (const b of buses) {
      const since = offSince(b.id, b.status, events);
      if (!since) continue;
      const ms = now - since.getTime();
      if (!best || ms > best.ms) best = { plate: b.plate, status: b.status, since, ms };
    }
    return best;
  }, [buses, events]);

  const selectedOffSince = selected ? offSince(selected.id, selected.status, events) : null;
  const selectedHistory = useMemo(
    () => (selected ? events.filter((e) => e.busId === selected.id) : []),
    [events, selected],
  );
  const selectedOffCount = useMemo(
    () => selectedHistory.filter((e) => isOffRoad(e.toStatus)).length,
    [selectedHistory],
  );

  const sm = selected ? metaFor(selected.status) : null;

  return (
    <div className="fd-screen">
      <header className="fd-appbar">
        <button type="button" className="fd-iconbtn" onClick={onClose} aria-label="Back">
          <FaArrowLeft />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1>Suspend / maintenance</h1>
          <p className="fd-sub">
            {counts.off === 0
              ? `All ${counts.total} bus${counts.total === 1 ? '' : 'es'} in service`
              : `${counts.off} of ${counts.total} buses off the road`}
          </p>
        </div>
      </header>

      <section className="fd-strip" style={{ gridTemplateColumns: '1.2fr 1fr 1fr 1fr' }}>
        <div className="fd-cell">
          <span className="fd-kicker">Fleet right now</span>
          <div className="fd-donutwrap">
            <StatusDonut counts={counts} />
            <div className="fd-donutlabel">
              <b className="fd-num">{counts.active} / {counts.total}</b>
              <span>on the road</span>
            </div>
          </div>
          <div className="fd-sevlegend">
            <span><span className="fd-dot fd-dot-ok" />{counts.active} in service</span>
            {counts.maintenance > 0 && <span><span className="fd-dot fd-dot-warn" />{counts.maintenance} maintenance</span>}
            {counts.suspended > 0 && <span><span className="fd-dot fd-dot-crit" />{counts.suspended} suspended</span>}
            {counts.for_hire > 0 && <span><span className="fd-dot fd-dot-brand" />{counts.for_hire} for hire</span>}
          </div>
        </div>

        <div className="fd-cell">
          <span className="fd-kicker">Children affected</span>
          <div className="fd-big fd-num">{childrenAffected}</div>
          <p className="fd-cellsub">
            {childrenAffected === 0
              ? 'No roster is sitting on a bus that is off the road'
              : 'On rosters whose bus is not in service — they need covering'}
          </p>
        </div>

        <div className="fd-cell">
          <span className="fd-kicker">Longest off the road</span>
          {longestOff ? (
            <>
              <div className="fd-big">{formatAge(longestOff.since)}</div>
              <p className="fd-cellsub">{longestOff.plate} · {metaFor(longestOff.status).label}</p>
            </>
          ) : counts.off === 0 ? (
            <>
              <div className="fd-big fd-num">0</div>
              <p className="fd-cellsub">Nothing is off the road</p>
            </>
          ) : (
            <>
              <div className="fd-big"><span className="fd-nodata" style={{ fontSize: 12 }}>Not recorded</span></div>
              <p className="fd-cellsub">
                {historyReady
                  ? 'This stint started before history was kept — logged from the next change onward.'
                  : 'Loading history…'}
              </p>
            </>
          )}
        </div>

        <div className="fd-cell">
          <span className="fd-kicker">Earning while parked</span>
          <div className="fd-big fd-num">{counts.for_hire}<small>for hire</small></div>
          <p className="fd-cellsub">Available, not on a school route</p>
        </div>
      </section>

      <div className="fd-split">
        <aside className="fd-rail">
          <div className="fd-railtop">
            <div className="fd-search">
              <FaSearch style={{ color: 'var(--fd-ink-400)', flex: '0 0 auto' }} />
              <input
                type="search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search bus, plate or driver" aria-label="Search buses"
              />
            </div>
            <div className="fd-chips">
              {([
                ['off', `Off the road · ${counts.off}`],
                ['maintenance', `Maintenance · ${counts.maintenance}`],
                ['suspended', `Suspended · ${counts.suspended}`],
                ['for_hire', `For hire · ${counts.for_hire}`],
                ['all', `All · ${counts.total}`],
              ] as const).map(([key, label]) => (
                <button
                  key={key} type="button" className="fd-chip"
                  aria-pressed={filter === key}
                  onClick={() => { setFilter(key); setSelectedId(null); }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="fd-raillist">
            {visible.length === 0 && (
              <div className="fd-empty">
                <FaCheck size={26} />
                <b>Every bus is in service</b>
                <span>Nothing is suspended, in maintenance, or out for hire.</span>
              </div>
            )}
            {grouped.map((g) => (
              <div key={g.label}>
                <p className="fd-groupname">{g.label}</p>
                {g.items.map((b) => {
                  const m = metaFor(b.status);
                  return (
                    <button
                      key={b.id} type="button"
                      className={`fd-item${b.status === 'active' ? ' fd-item-read' : ''}`}
                      aria-current={selected?.id === b.id ? 'true' : undefined}
                      onClick={() => setSelectedId(b.id)}
                    >
                      <span className={`fd-ig ${m.ig}`}>{m.icon}</span>
                      <span className="fd-itxt">
                        <span className="fd-ititle" style={{ display: 'block' }}>{b.name}</span>
                        <span className="fd-imeta">
                          <span>{b.plate}</span>
                          <span className="fd-sep">·</span>
                          <span>{b.childCount} child{b.childCount === 1 ? '' : 'ren'}</span>
                          {b.lastTripFinished && (
                            <>
                              <span className="fd-sep">·</span>
                              <span>last ran {b.lastTripFinished.toLocaleDateString()}</span>
                            </>
                          )}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </aside>

        <section className="fd-detail">
          {!selected && (
            <div className="fd-empty" style={{ paddingTop: events.length > 0 ? 48 : 120 }}>
              <FaCheck size={30} />
              <b>Every bus is on the road</b>
              <span>Nothing is suspended, in maintenance, or out for hire.</span>
              {events.length > 0 && (
                <div className="fd-card fd-block" style={{ margin: '28px auto 0', maxWidth: 520, textAlign: 'left' }}>
                  <h3>Status history<span className="fd-rest">{events.length} event{events.length === 1 ? '' : 's'}</span></h3>
                  <div className="fd-tl">
                    {events.slice(0, 20).map((e) => (
                      <div key={e.id} className={`fd-tlrow ${tlClass(e.toStatus)}`}>
                        <b>{e.plate} · {eventTitle(e.toStatus)}</b>
                        <span>
                          {metaFor(e.fromStatus).label} → {metaFor(e.toStatus).label}
                          {' · '}
                          {formatWhen(e.at)}
                          {' · '}
                          {sourceLabel(e.source)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {selected && sm && (
            <>
              <div className="fd-hero">
                <div className="fd-heroinner">
                  <div style={{ minWidth: 0 }}>
                    <span className="fd-pill fd-pill-onbrand">{sm.label}</span>
                    <h2>
                      {selected.status === 'active'
                        ? 'On the road and running its route'
                        : selected.status === 'for_hire'
                          ? 'Available for hire, not on a school route'
                          : selected.childCount > 0
                            ? `Off the road — ${selected.childCount} child${selected.childCount === 1 ? '' : 'ren'} need covering`
                            : 'Off the road, with no roster attached'}
                    </h2>
                    <div className="fd-herometa">
                      <span className="fd-avatar">{monogram(selected.name)}</span>
                      <span style={{ color: '#fff', fontWeight: 600 }}>{selected.name}</span>
                      <span className="fd-plate">{selected.plate}</span>
                      {selected.drivers[0] && (<><span>·</span><span>{selected.drivers[0]}</span></>)}
                      <span>·</span>
                      <span>
                        {selected.lastTripFinished
                          ? `Last ran ${selected.lastTripFinished.toLocaleDateString()}`
                          : 'No finished trip on file'}
                      </span>
                    </div>
                  </div>
                  <div className="fd-heroactions">
                    <button type="button" className="fd-btn fd-btn-white fd-btn-sm" onClick={() => onOpenBus(selected.id)}>
                      Open bus
                    </button>
                  </div>
                </div>
              </div>

              <div className="fd-tiles">
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Status</span>
                  <p className="fd-tileval">
                    <span className={`fd-pill ${sm.pill}`}><span className={`fd-dot ${sm.dot}`} />{sm.label}</span>
                  </p>
                  <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>Set on the bus page</p>
                </div>
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Off the road since</span>
                  {selected.status === 'active' ? (
                    <>
                      <p className="fd-tileval">In service</p>
                      <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>Not currently off the road</p>
                    </>
                  ) : selectedOffSince ? (
                    <>
                      <p className="fd-tileval">{formatAge(selectedOffSince)}</p>
                      <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>{formatWhen(selectedOffSince)}</p>
                    </>
                  ) : (
                    <>
                      <p className="fd-tileval"><span className="fd-nodata">Not recorded</span></p>
                      <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>
                        This stint started before history was kept
                      </p>
                    </>
                  )}
                </div>
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Children on roster</span>
                  <p className="fd-tileval fd-num">{selected.childCount}</p>
                  <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>
                    {selected.status === 'active' ? 'Being carried' : 'Riding something else'}
                  </p>
                </div>
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Times off the road</span>
                  <p className="fd-tileval fd-num">{selectedOffCount}</p>
                  <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>
                    {selectedOffCount === 0
                      ? 'No suspend or hire logged yet'
                      : 'Logged suspend / maintenance / hire'}
                  </p>
                </div>
              </div>

              <div className="fd-grid">
                <div>
                  <div className="fd-card fd-block">
                    <h3>Status history<span className="fd-rest">{selectedHistory.length} event{selectedHistory.length === 1 ? '' : 's'}</span></h3>
                    {selectedHistory.length === 0 ? (
                      <p className="fd-tiny fd-muted" style={{ margin: 0 }}>
                        Nothing logged for this bus yet. Suspend, mark for hire, or put it in
                        maintenance — then activate it again — and both sides of that stay here.
                        Older changes from before this log existed are not reconstructed.
                      </p>
                    ) : (
                      <div className="fd-tl">
                        {selectedHistory.map((e) => (
                          <div key={e.id} className={`fd-tlrow ${tlClass(e.toStatus)}`}>
                            <b>{eventTitle(e.toStatus)}</b>
                            <span>
                              {metaFor(e.fromStatus).label} → {metaFor(e.toStatus).label}
                              {' · '}
                              {formatWhen(e.at)}
                              {e.childCount > 0 ? ` · ${e.childCount} on roster` : ''}
                              {' · '}
                              {sourceLabel(e.source)}
                              {e.byEmail ? ` · ${e.byEmail}` : ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="fd-card fd-block" style={{ marginTop: 16 }}>
                    <h3>The fleet, bus by bus<span className="fd-rest">{counts.total} buses</span></h3>
                    <div className="fd-bars">
                      {ordered.slice(0, 12).map((b) => {
                        const max = Math.max(1, ...ordered.map((x) => x.childCount));
                        return (
                          <div className="fd-bar" key={b.id}>
                            <span className="fd-lbl" style={b.id === selected.id ? { color: 'var(--fd-brand-700)' } : undefined}>
                              {b.plate}
                            </span>
                            <span className="fd-trk">
                              <i
                                className={`fd-fil ${b.status === 'active' ? 'fd-fil-ok' : b.status === 'suspended' ? 'fd-fil-crit' : b.status === 'for_hire' ? '' : 'fd-fil-warn'}`}
                                style={{ width: `${Math.max(6, (b.childCount / max) * 100)}%` }}
                              />
                            </span>
                            <span className="fd-val fd-num">{b.childCount}</span>
                          </div>
                        );
                      })}
                    </div>
                    <p className="fd-tiny fd-muted" style={{ margin: '12px 0 0' }}>
                      Bar length is the roster each bus carries; the colour is its status. A long amber or red
                      bar is the row that matters — a big roster with no bus under it.
                    </p>
                  </div>

                  <div className="fd-card fd-block" style={{ marginTop: 16 }}>
                    <h3>Fleet log<span className="fd-rest">{events.length} event{events.length === 1 ? '' : 's'}</span></h3>
                    {events.length === 0 ? (
                      <p className="fd-tiny fd-muted" style={{ margin: 0 }}>
                        Empty until the next suspend, hire, maintenance, or activate. That is when
                        the clock starts — past stints are not invented.
                      </p>
                    ) : (
                      <div className="fd-tl">
                        {events.slice(0, 20).map((e) => (
                          <div key={e.id} className={`fd-tlrow ${tlClass(e.toStatus)}`}>
                            <b>{e.plate} · {eventTitle(e.toStatus)}</b>
                            <span>
                              {metaFor(e.fromStatus).label} → {metaFor(e.toStatus).label}
                              {' · '}
                              {formatWhen(e.at)}
                              {' · '}
                              {sourceLabel(e.source)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <div className="fd-card fd-buscard">
                    <div className="fd-bctop">
                      <span className="fd-avatar" style={{ borderColor: 'rgba(255,255,255,.5)' }}>{monogram(selected.name)}</span>
                      <div>
                        <b>{selected.name}</b>
                        <span>{selected.plate}{selected.drivers[0] ? ` · ${selected.drivers[0]}` : ''}</span>
                      </div>
                    </div>
                    <div className="fd-bcinner">
                      <div className="fd-bcrow">
                        <span>Status</span>
                        <span><span className={`fd-pill ${sm.pill}`}><span className={`fd-dot ${sm.dot}`} />{sm.label}</span></span>
                      </div>
                      <div className="fd-bcrow"><span>Children on roster</span><span className="fd-num">{selected.childCount}</span></div>
                      <div className="fd-bcrow">
                        <span>Off since</span>
                        <span>
                          {selected.status === 'active'
                            ? 'In service'
                            : selectedOffSince
                              ? formatAge(selectedOffSince)
                              : <span className="fd-nodata">Not recorded</span>}
                        </span>
                      </div>
                      <div className="fd-bcrow"><span>Times off the road</span><span className="fd-num">{selectedOffCount}</span></div>
                      <div className="fd-bcrow"><span>Distance · 30 d</span><span className="fd-num">{Math.round(selected.km30d).toLocaleString()} km</span></div>
                      <div className="fd-bcrow">
                        <span>Last finished trip</span>
                        <span>{selected.lastTripFinished ? selected.lastTripFinished.toLocaleDateString() : <span className="fd-nodata">None</span>}</span>
                      </div>
                      <div className="fd-bcrow">
                        <span>Driver</span>
                        <span>{selected.drivers[0] ?? <span className="fd-nodata">Unassigned</span>}</span>
                      </div>
                    </div>
                  </div>

                  <div className="fd-card fd-block" style={{ marginTop: 16 }}>
                    <h3>Do next</h3>
                    <button type="button" className="fd-btn fd-btn-primary fd-btn-sm fd-btn-block" onClick={() => onOpenBus(selected.id)}>
                      Open bus page
                    </button>
                    <p className="fd-tiny fd-muted" style={{ margin: '12px 0 0' }}>
                      Status is changed on the bus page, so the one flow that writes it stays in one place.
                    </p>
                  </div>

                  {selected.childCount > 0 && selected.status !== 'active' && (
                    <div className="fd-card fd-block" style={{ marginTop: 16 }}>
                      <h3>Parents told?</h3>
                      <p className="fd-tiny fd-muted" style={{ margin: 0 }}>
                        {selected.childCount} famil{selected.childCount === 1 ? 'y is' : 'ies are'} attached to this bus.
                        Putting it back in service silently is how a parent ends up waiting at the wrong stop —
                        worth an announcement either way.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

/** Four-part ring: in service / maintenance / suspended / for hire. */
function StatusDonut({ counts }: { counts: { active: number; maintenance: number; suspended: number; for_hire: number; total: number } }) {
  const r = 30, c = 2 * Math.PI * r;
  const total = counts.total || 1;
  const segs = [
    { n: counts.active, color: '#16a34a' },
    { n: counts.maintenance, color: '#d97706' },
    { n: counts.suspended, color: '#dc2626' },
    { n: counts.for_hire, color: '#6f4ff0' },
  ];
  let offset = 0;
  return (
    <svg width="76" height="76" viewBox="0 0 76 76" aria-hidden="true">
      <circle cx="38" cy="38" r={r} fill="none" stroke="#efe9ff" strokeWidth="12" />
      {segs.map((s, i) => {
        if (s.n <= 0) return null;
        const len = (s.n / total) * c;
        const el = (
          <circle
            key={i} cx="38" cy="38" r={r} fill="none" stroke={s.color} strokeWidth="12"
            strokeDasharray={`${len.toFixed(1)} ${c.toFixed(1)}`}
            strokeDashoffset={`${(-offset).toFixed(1)}`}
            transform="rotate(-90 38 38)"
          />
        );
        offset += len;
        return el;
      })}
    </svg>
  );
}
